import { t } from '@repo/i18n';
import { filterPrimaryDocuments } from '@repo/shared/lib';
import { ReportAutofillStatus, ReportType } from '@repo/shared/types';
import type { RouterOutputs } from '@/providers/trpc-client';

// ─── Step definitions ─────────────────────────────────────────────────────────

export type WizardReport = RouterOutputs['report']['getReport'];

export type WizardLoaderData = {
  report: WizardReport;
};

const KYCFormSteps = {
  reportType: 'reportType',
  upload: 'upload',
  additional: 'additional',
  clientInformation: 'clientInformation',
} as const;
type KYCFormSteps = (typeof KYCFormSteps)[keyof typeof KYCFormSteps];
const KYBFormSteps = {
  reportType: 'reportType',
  upload: 'upload',
  additional: 'additional',
  keyMembers: 'keyMembers',
  companyInformation: 'companyInformation',
} as const;
type KYBFormSteps = (typeof KYBFormSteps)[keyof typeof KYBFormSteps];
export const FormSteps = {
  ...KYCFormSteps,
  ...KYBFormSteps,
} as const;
export type FormSteps = (typeof FormSteps)[keyof typeof FormSteps];
export const FormStepsKeys = Object.keys(FormSteps) as [keyof typeof FormSteps];

export const KYB_STEP_META: Record<
  KYBFormSteps,
  { index: number; shortTitle: string; title: string; description: string }
> = {
  reportType: {
    index: 1,
    get shortTitle() {
      return t('reports.selectReportType');
    },
    get title() {
      return t('reports.selectReportType');
    },
    get description() {
      return t('reports.selectTheTypeOfReportYouWantToCreate');
    },
  },
  upload: {
    index: 2,
    get shortTitle() {
      return t('extra.uploadCompanyDocs');
    },
    get title() {
      return t('reports.uploadCompanyLegalDocuments');
    },
    get description() {
      return t('reports.selectAndUploadDocumentsForAtLeastOneOption');
    },
  },
  additional: {
    index: 3,
    get shortTitle() {
      return t('extra.additionalDocuments');
    },
    get title() {
      return t('reports.uploadAdditionalDocumentsOptional');
    },
    get description() {
      return t('reports.anySupportingDocumentsRelatedToTheBusinessIfAvailable');
    },
  },
  keyMembers: {
    index: 4,
    get shortTitle() {
      return t('reports.keyMembers');
    },
    get title() {
      return t('reports.enterKeyStakeholdersMembers');
    },
    get description() {
      return t('reports.addTheKeyIndividualsAssociatedWithTheBusinessDirectors');
    },
  },
  companyInformation: {
    index: 5,
    get shortTitle() {
      return t('extra.companyForm');
    },
    get title() {
      return t('reports.completeBusinessInformationForm');
    },
    get description() {
      return t('reports.checkAndFinalizeTheCompanySDetails');
    },
  },
};

export const KYB_PREV_STEP: Partial<Record<KYBFormSteps, KYBFormSteps>> = {
  reportType: 'reportType',
  additional: 'upload',
  keyMembers: 'additional',
  companyInformation: 'keyMembers',
};

export const KYB_NEXT_STEP: Partial<Record<KYBFormSteps, KYBFormSteps>> = {
  reportType: 'reportType',
  upload: 'additional',
  additional: 'keyMembers',
  keyMembers: 'companyInformation',
  // companyInformation: no next — Submit is in CompanyInformationForm
};

// ─── Step definitions ─────────────────────────────────────────────────────────

export const KYC_STEP_META: Record<
  KYCFormSteps,
  { index: number; shortTitle: string; title: string; description: string }
> = {
  reportType: {
    index: 1,
    get shortTitle() {
      return t('reports.selectReportType');
    },
    get title() {
      return t('reports.selectReportType');
    },
    get description() {
      return t('reports.selectTheTypeOfReportYouWantToCreate');
    },
  },
  upload: {
    index: 2,
    get shortTitle() {
      return t('extra.uploadClientId');
    },
    get title() {
      return t('reports.uploadClientIdentificationId');
    },
    get description() {
      return t('reports.provideTheClientSIdentityDocumentForVerification');
    },
  },
  additional: {
    index: 3,
    get shortTitle() {
      return t('extra.additionalDocuments');
    },
    get title() {
      return t('reports.uploadAdditionalDocumentsOptional');
    },
    get description() {
      return t('reports.anySupportingDocumentsRelatedToTheClientIfAvailable');
    },
  },
  clientInformation: {
    index: 4,
    get shortTitle() {
      return t('extra.completeClientForm');
    },
    get title() {
      return t('reports.completeClientInformationForm');
    },
    get description() {
      return t('reports.checkAndFinalizeTheClientSDetails');
    },
  },
};

export const KYC_PREV_STEP: Partial<Record<KYCFormSteps, KYCFormSteps>> = {
  additional: 'upload',
  clientInformation: 'additional',
};

export const KYC_NEXT_STEP: Partial<Record<KYCFormSteps, KYCFormSteps>> = {
  upload: 'additional',
  additional: 'clientInformation',
};

export type StepEntry = {
  key: string;
  index: number;
  shortTitle: string;
  title: string;
  description: string;
};

function excludeReportTypeStep(steps: StepEntry[], isNewReport: boolean): StepEntry[] {
  if (isNewReport) {
    return steps;
  } else {
    return steps
      .filter((step) => step.key !== 'reportType')
      .map((step) => ({
        ...step,
        index: step.index - 1,
      }));
  }
}

export function getStepsForReportType(reportType: ReportType, isNewReport: boolean): StepEntry[] {
  const meta = reportType === ReportType.KYB ? KYB_STEP_META : KYC_STEP_META;
  const steps =
    reportType === ReportType.KYB ? Object.keys(KYBFormSteps) : Object.keys(KYCFormSteps);
  return excludeReportTypeStep(
    steps.map((key) => ({
      key,
      ...(meta as Record<string, Omit<StepEntry, 'key'>>)[key]!,
    })),
    isNewReport,
  );
}

export function getNextStep(reportType: ReportType, currentStep: string): string | undefined {
  const map = reportType === ReportType.KYB ? KYB_NEXT_STEP : KYC_NEXT_STEP;
  return (map as Record<string, string | undefined>)[currentStep];
}

export function getPrevStep(reportType: ReportType, currentStep: string): string | undefined {
  const map = reportType === ReportType.KYB ? KYB_PREV_STEP : KYC_PREV_STEP;
  return (map as Record<string, string | undefined>)[currentStep];
}

export function getStepMeta(reportType: ReportType, step: string): StepEntry | undefined {
  const meta = reportType === ReportType.KYB ? KYB_STEP_META : KYC_STEP_META;
  const entry = (meta as Record<string, Omit<StepEntry, 'key'>>)[step];
  if (!entry) return undefined;
  return { key: step, ...entry };
}

/**
 * Whether each wizard step is locked in the sidebar (user cannot navigate there yet).
 */
export function getWizardStepLocks(
  report: WizardReport | undefined,
  reportType: ReportType,
  isNewReport: boolean = false,
): Record<FormSteps, boolean> {
  if (isNewReport) {
    return {
      reportType: false,
      upload: true,
      additional: true,
      clientInformation: true,
      keyMembers: true,
      companyInformation: true,
    };
  }
  const primary = filterPrimaryDocuments(report?.documents ?? []);
  const primaryAccepted =
    primary.length > 0 &&
    primary.every((d) => d.isAcceptedByUser === true && d.extractionStatus === 'COMPLETED');

  const autofillDone = report?.autofillStatus === ReportAutofillStatus.FINISHED;

  const additionalLocked = !primaryAccepted;

  const clientOrMembersLocked =
    !autofillDone || report?.autofillStatus === ReportAutofillStatus.PROCESSING;

  return {
    reportType: false,
    upload: !report?.reportType,
    additional: additionalLocked,
    clientInformation: reportType === ReportType.KYC ? clientOrMembersLocked : true,
    keyMembers: reportType === ReportType.KYB ? clientOrMembersLocked : true,
    companyInformation: reportType === ReportType.KYB ? clientOrMembersLocked : true,
  };
}
